import { buildJwt } from '../context/TokenGenerator';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const SERVER_ENVIRONMENT = process.env.REACT_APP_SERVER_ENVIRONMENT || 'dev';
const ALLOWED_DOMAINS_API = process.env.REACT_APP_API_URL + '/api/allowed-domains';

const getAuthHeaders = (token, jwtToken) => ({
  'Authorization': `Bearer ${jwtToken}`,
  'XGoogleAuthorization': `Bearer ${token}`,
  'Content-Type': 'application/json',
});

const getEnvParam = async () => {
  const env = process.env.REACT_APP_SERVER_ENVIRONMENT;
  if (env === 'stage') return 'stage';
  if (env === 'development' || env === 'dev') return 'dev';
  if (env === 'prod' || env === 'production') return 'prod';
  return 'local';
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || 'Request failed');
  }
  return response.json();
};

// Helper function to make API requests with automatic token refresh on 401
const fetchWithTokenRefresh = async (url, options, token, retried = false) => {
  const response = await fetch(url, options);

  // If we get a 401 and haven't retried yet, refresh the JWT token and retry
  if (response.status === 401 && !retried) {
    console.log('Received 401, refreshing JWT token and retrying...');
    try {
      const newJwtToken = await buildJwt();
      // Update the Authorization header with the new token
      const newOptions = {
        ...options,
        headers: {
          ...options.headers,
          'Authorization': `Bearer ${newJwtToken}`,
        },
      };
      return fetchWithTokenRefresh(url, newOptions, token, true);
    } catch (error) {
      console.error('Failed to refresh token:', error);
      throw new Error('Session expired. Please log in again.');
    }
  }

  return response;
};

// Helper to add env, guest, and user info to payload
const addEnvToPayload = (data = {}, user = null, isGuest = false) => {
  const payload = {
    ...data,
    env: SERVER_ENVIRONMENT,
    guest: isGuest,
  };

  // Add user info only if guest mode is enabled
  if (isGuest && user) {
    payload.user_id = user.id;
  }

  return payload;
};

// Helper to add env, guest, and user info as query params for GET requests
const addEnvParam = (url, user = null, isGuest = false) => {
  const params = new URLSearchParams();
  params.append('env', SERVER_ENVIRONMENT);
  params.append('guest', isGuest);

  // Add user info only if guest mode is enabled
  if (isGuest && user) {
    params.append('user_id', user.id);
  }

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${params.toString()}`;
};

export const api = {

  // Fetch allowed domains from API
  async fetchAllowedDomains(token, jwtToken) {
    try {
      const env = await getEnvParam();
      // const response = await fetch(`${ALLOWED_DOMAINS_API}?env=${env}&guest=${process.env.REACT_APP_BYPASS_AUTH}`);
      const response = await fetch(`${ALLOWED_DOMAINS_API}?env=${env}&guest=${process.env.REACT_APP_BYPASS_AUTH}`, {
          headers: getAuthHeaders(token, jwtToken),
      });
       
      return handleResponse(response);
    } catch (error) {
      console.error('Error fetching allowed domains:', error);
      return null;
    }
  },

  // Health check
  async health(user = null, isGuest = false) {
    const response = await fetch(addEnvParam(`${API_BASE_URL}/api/health`, user, isGuest));
    return handleResponse(response);
  },

  // Get user's drafts
  async getDrafts(token, jwtToken, user = null, isGuest = false) {
    const response = await fetchWithTokenRefresh(addEnvParam(`${API_BASE_URL}/api/guides/drafts`, user, isGuest), {
      headers: getAuthHeaders(token, jwtToken),
    }, token);
    return handleResponse(response);
  },

  // Get all published guides
  async getPublished(token, jwtToken, user = null, isGuest = false) {
    const response = await fetchWithTokenRefresh(addEnvParam(`${API_BASE_URL}/api/guides/published`, user, isGuest), {
      headers: getAuthHeaders(token, jwtToken),
    }, token);
    return handleResponse(response);
  },

  // Get single guide
  async getGuide(token, jwtToken, id, user = null, isGuest = false) {
    const response = await fetchWithTokenRefresh(addEnvParam(`${API_BASE_URL}/api/guides/${id}`, user, isGuest), {
      headers: getAuthHeaders(token, jwtToken),
    }, token);
    return handleResponse(response);
  },

  // Create new guide
  async createGuide(token, jwtToken, guideData, user = null, isGuest = false) {
    const response = await fetchWithTokenRefresh(`${API_BASE_URL}/api/guides`, {
      method: 'POST',
      headers: getAuthHeaders(token, jwtToken),
      body: JSON.stringify(addEnvToPayload(guideData, user, isGuest)),
    }, token);
    return handleResponse(response);
  },

  // Update guide
  async updateGuide(token, jwtToken, id, guideData, user = null, isGuest = false) {
    const response = await fetchWithTokenRefresh(`${API_BASE_URL}/api/guides/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(token, jwtToken),
      body: JSON.stringify(addEnvToPayload(guideData, user, isGuest)),
    }, token);
    return handleResponse(response);
  },

  // Publish guide
  async publishGuide(token, jwtToken, id, user = null, isGuest = false) {
    const response = await fetchWithTokenRefresh(`${API_BASE_URL}/api/guides/${id}/publish`, {
      method: 'POST',
      headers: getAuthHeaders(token, jwtToken),
      body: JSON.stringify(addEnvToPayload({}, user, isGuest)),
    }, token);
    return handleResponse(response);
  },

  // Duplicate guide
  async duplicateGuide(token, jwtToken, id, user = null, isGuest = false) {
    const response = await fetchWithTokenRefresh(`${API_BASE_URL}/api/guides/${id}/duplicate`, {
      method: 'POST',
      headers: getAuthHeaders(token, jwtToken),
      body: JSON.stringify(addEnvToPayload({}, user, isGuest)),
    }, token);
    return handleResponse(response);
  },

  // Delete guide
  async deleteGuide(token, jwtToken, id, user = null, isGuest = false) {
    const response = await fetchWithTokenRefresh(addEnvParam(`${API_BASE_URL}/api/guides/${id}`, user, isGuest), {
      method: 'DELETE',
      headers: getAuthHeaders(token, jwtToken),
    }, token);
    return handleResponse(response);
  },

  // Generate study summary from article URL using n8n webhook
  // BACKUP (old implementation using internal API):
  // async generateStudySummary(token, id, user = null, isGuest = false, url) {
  //   const response = await fetch(`${API_BASE_URL}/api/generate-study-summary`, {
  //     method: 'POST',
  //     headers: getAuthHeaders(token),
  //     body: JSON.stringify(addEnvToPayload({ url }, user, isGuest)),
  //   });
  //   return handleResponse(response);
  // },
  async generateStudySummary(token, id, user = null, isGuest = false, url, wordCount = 150) {
    const webhookUrl = 'https://n8n-app.victoriousmoss-fceba56c.eastus2.azurecontainerapps.io/webhook/quickguide-summarize';
    const params = new URLSearchParams({
      articleUrl: url,
      wordCount: wordCount.toString()
    });

    const response = await fetch(`${webhookUrl}?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || 'Request failed');
    }

    return response.json();
  },
  // Upload thumbnail
  async uploadThumbnail(token, jwtToken, id, base64Image, user = null, isGuest = false, isStudyImage = false) {
    const formData = new FormData();
    formData.append('thumbnail', base64Image); // Base64 string of the image
    formData.append('env', SERVER_ENVIRONMENT);
    formData.append('guest', isGuest);
    formData.append('studyimg', isStudyImage);

    // Always add user_id if user is available
    if (user && user.id) {
      formData.append('user_id', user.id);
    }

    const response = await fetchWithTokenRefresh(`${API_BASE_URL}/api/guides/${id}/thumbnail`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${jwtToken}`,
        'XGoogleAuthorization': `Bearer ${token}`,
      },
      body: formData,
    }, token);
    return handleResponse(response);
  },
};

export default api;
